/**
 * SyncEvent Types
 * Based on the BYOK SyncEvent tracking system data model
 */

// Event type constants
export const EVENT_TYPES = {
  DEVICE_LINK_CONFLICT: 'device_link_conflict',
  DEVICE_LINK_CLAIMED: 'device_link_claimed',
  DEVICE_AUTHENTICATED: 'device_authenticated',
  SYNC_HANDSHAKE: 'sync_handshake',
  FILE_UPLOAD: 'file_upload',
  FILE_DOWNLOAD: 'file_download',
  CONFLICT_CREATED: 'conflict_created',
  CONFLICT_RESOLVED: 'conflict_resolved',
  ERROR: 'error',
} as const;

export type EventType = typeof EVENT_TYPES[keyof typeof EVENT_TYPES];

// File type constants
export const FILE_TYPES = {
  TEXT: 'text',
  WIKI: 'wiki',
  GRID: 'grid',
  OUTLINE: 'outline',
  CARDSTACK: 'cardstack',
} as const;

export type FileType = typeof FILE_TYPES[keyof typeof FILE_TYPES];

// Corruption detection categories
export const CORRUPTION_CATEGORIES = {
  NULL_BYTES: 'NULL_BYTES',
  CONTROL_CHARS: 'CONTROL_CHARS',
  TLS_CERT_DATA: 'TLS_CERT_DATA',
  ENCODING_GARBAGE: 'ENCODING_GARBAGE',
  REPEATED_NULL_DISPLAY: 'REPEATED_NULL_DISPLAY',
  REPEATED_AT: 'REPEATED_AT',
  REPEATED_PERCENT: 'REPEATED_PERCENT',
  REPEATED_QUESTION: 'REPEATED_QUESTION',
  MIXED_GARBAGE: 'MIXED_GARBAGE',
} as const;

export type CorruptionCategory = typeof CORRUPTION_CATEGORIES[keyof typeof CORRUPTION_CATEGORIES];

// Quarantine reason values
export const QUARANTINE_REASONS = {
  TLS_BUFFER_LEAK: 'TLS_BUFFER_LEAK',
  MDNS_CONTAMINATION: 'MDNS_CONTAMINATION',
  FILESYSTEM_METADATA: 'FILESYSTEM_METADATA',
  SYNC_QUEUE_LEAK: 'SYNC_QUEUE_LEAK',
  HIGH_CORRUPTION_RATIO: 'HIGH_CORRUPTION_RATIO',
  SEVERE_TRUNCATION: 'SEVERE_TRUNCATION',
  EMPTY_UPLOAD: 'EMPTY_UPLOAD',
} as const;

export type QuarantineReason = typeof QUARANTINE_REASONS[keyof typeof QUARANTINE_REASONS];

// Human-readable quarantine reason labels
export const QUARANTINE_REASON_LABELS: Record<QuarantineReason, string> = {
  TLS_BUFFER_LEAK: 'TLS/Certificate Buffer Leak',
  MDNS_CONTAMINATION: 'mDNS/Network Discovery',
  FILESYSTEM_METADATA: 'File System Metadata Leak',
  SYNC_QUEUE_LEAK: 'Sync Queue Buffer Leak',
  HIGH_CORRUPTION_RATIO: 'High Corruption Ratio',
  SEVERE_TRUNCATION: 'Severe Content Truncation',
  EMPTY_UPLOAD: 'Empty Upload (zero bytes of text content)',
};

// Conflict resolution action types
export const CONFLICT_ACTIONS = {
  OVERWRITE_SERVER: 'overwrite_server',
  KEEP_BOTH: 'keep_both',
  DISCARD_DEVICE: 'discard_device',
} as const;

export type ConflictAction = typeof CONFLICT_ACTIONS[keyof typeof CONFLICT_ACTIONS];

// Human-readable conflict action labels
export const CONFLICT_ACTION_LABELS: Record<ConflictAction, string> = {
  overwrite_server: 'Overwrite Server (Device Won)',
  keep_both: 'Keep Both Versions',
  discard_device: 'Discard Device (Server Won)',
};

/**
 * Name deduplication detail entry
 * Represents a single file that was renamed with a _1, _2 suffix
 */
export interface NameDeduplicationDetail {
  file_id: string;
  project_id: string;
  original_name: string;
  deduplicated_name: string;
}

/**
 * Conflict created request/response summary
 */
export interface ConflictCreatedRequestSummary {
  device_timestamp: string;
  server_updated_at: string;
}

export interface ConflictCreatedResponseSummary {
  conflict_id: string;
  server_file_id: string;
  device_file_id: string;
  reason: string;
}

/**
 * Conflict resolved request/response summary
 */
export interface ConflictResolvedRequestSummary {
  action: ConflictAction;
  conflict_id: string;
}

export interface ConflictResolvedResponseSummary {
  action_taken: ConflictAction;
  server_file_id: string;
  device_file_id: string;
  outcome: string;
}

/**
 * Device link conflict (ID collision) request/response summary
 */
export interface DeviceLinkConflictRequestSummary {
  device_id: string;
  attempted_by_user_id: string;
  attempted_by_email: string;
  existing_owner_user_id: string;
  existing_owner_email: string;
}

export interface DeviceLinkConflictResponseSummary {
  status: 'conflict';
  blocked: boolean;
}

/**
 * Request/Response summary types by event type
 */
export interface SyncHandshakeRequestSummary {
  projects_count: number;
  files_to_upload_count: number;
  total_files_on_device: number;
}

export interface SyncHandshakeResponseSummary {
  upload_count: number;
  download_count: number;
  remove_count: number;
  projects_to_create: number;
  projects_to_remove: number;
  projects_to_update: number;
  conflict_detected: boolean;
  reconciliation_performed: boolean;
  new_files_created: string[];
  files_to_upload: string[];
  files_to_download: string[];
  files_to_remove: string[];
  // Deduplication tracking (new)
  name_deduplication_count?: number;
  name_deduplications?: Array<{ original: string; new: string }>;
  // Conflict tracking (new)
  conflicts_created_count?: number;
  conflicts_created?: Array<{ file_name: string; conflict_id: string }>;
}

/**
 * Raw bytes diagnostic for analyzing upload content issues
 */
export interface RawBytesDiagnostic {
  first_200_hex: string;
  last_200_hex: string;
  first_200_readable: string;
  last_200_readable: string;
  null_byte_count: number;
  null_percentage: number;
}

/**
 * Suspicious file name detection result
 * Present when a file has a suspicious name (null/absent for normal names)
 */
export interface SuspiciousFileName {
  issue: 'extension_only' | 'empty_name' | 'invalid_characters_only';
  file_name: string;
  description: string;
}

/**
 * Upload validation details included in file upload requests
 */
export interface UploadValidation {
  content_length_header?: number;
  actual_bytes_received?: number;
  size_mismatch?: boolean;
  validation_passed?: boolean;
  expected_file_size?: number | null;
  multipart_overhead?: number;
  raw_bytes_diagnostic?: RawBytesDiagnostic;
  suspicious_file_name?: SuspiciousFileName | null;
  [key: string]: unknown; // Allow other validation fields
}

export interface FileUploadRequestSummary {
  conflict_upload: boolean;
  original_file_id: string | null;
  upload_validation?: UploadValidation;
}

export interface FileUploadResponseSummary {
  target_file_id: string;
  timestamp: string;
}

export interface FileDownloadRequestSummary {
  requested_file_id: string;
}

export interface FileDownloadResponseSummary {
  deduplicated_name: string;
  original_name: string;
  name_was_changed: boolean;
}

export type RequestSummary = 
  | SyncHandshakeRequestSummary 
  | FileUploadRequestSummary 
  | FileDownloadRequestSummary 
  | ConflictCreatedRequestSummary
  | ConflictResolvedRequestSummary
  | DeviceLinkConflictRequestSummary
  | Record<string, unknown>;

export type ResponseSummary = 
  | SyncHandshakeResponseSummary 
  | FileUploadResponseSummary 
  | FileDownloadResponseSummary 
  | ConflictCreatedResponseSummary
  | ConflictResolvedResponseSummary
  | DeviceLinkConflictResponseSummary
  | Record<string, unknown>;

/**
 * Main SyncEvent model
 */
export interface SyncEvent {
  // Identity
  id: string;
  session_id: string;

  // User/Device
  user: string | null;
  user_email: string | null;
  device: string | null;
  device_code: string | null;
  device_name: string | null;

  // Event Classification
  event_type: EventType;
  endpoint: string | null;

  // File Context
  file_id: string | null;
  file_name: string | null;
  file_type: FileType | null;
  project_id: string | null;
  project_name: string | null;
  file_size_bytes: number | null;

  // Firmware
  firmware_version: string | null;
  pico_version: string | null;

  // Sync Metadata
  is_first_sync: boolean;

  // Name Deduplication Tracking
  has_name_deduplication: boolean;
  name_deduplication_count: number | null;
  name_deduplication_total_files: number | null;
  name_deduplication_details: NameDeduplicationDetail[] | null;
  is_mass_deduplication: boolean;

  // Content Capture (primarily for file_upload events)
  content_raw: string | null;
  content_processed: string | null;
  content_size_raw: number | null;
  content_encoding: string | null;
  content_hash_raw: string | null;
  content_hash_processed: string | null;

  // Corruption Detection
  has_suspicious_chars: boolean;
  suspicious_char_count: number | null;
  suspicious_details: string | null;

  // Quarantine System
  was_quarantined: boolean;
  quarantine_reason: QuarantineReason | null;

  // Request/Response Data
  request_headers: Record<string, string> | null;
  request_summary: RequestSummary | null;
  response_summary: ResponseSummary | null;

  // Outcome
  status_code: number | null;
  success: boolean;
  error_message: string | null;

  // Timing
  timestamp: string;
  duration_ms: number | null;
}

/**
 * Paginated response from API
 */
export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

/**
 * Session summary for listing sessions
 */
export interface SessionSummary {
  session_id: string;
  timestamp: string;
  event_count: number;
  user_email: string | null;
  device_code: string | null;
  device_name: string | null;
  has_corruption: boolean;
  has_errors: boolean;
  upload_count: number;
  download_count: number;
}

/**
 * Stats aggregation
 */
export interface SyncEventStats {
  total_events: number;
  events_by_type: Record<EventType, number>;
  corruption_count: number;
  corruption_rate: number;
  quarantined_count: number;
  quarantined_last_24h: number;
  error_count: number;
  unique_users: number;
  unique_devices: number;
  events_last_24h: number;
  corruption_last_24h: number;
  // Deduplication stats (new)
  deduplication_count: number;
  deduplication_last_24h: number;
  mass_deduplication_count: number;
  mass_deduplication_last_24h: number;
  // Conflict stats (new)
  conflicts_created_count: number;
  conflicts_resolved_count: number;
  conflicts_pending_count: number;
}

/**
 * Device summary for grouping
 */
export interface DeviceSummary {
  device_code: string;
  device_name: string | null;
  user_email: string | null;
  total_events: number;
  corruption_count: number;
  last_sync: string;
  firmware_version: string | null;
}

/**
 * User summary for grouping
 */
export interface UserSummary {
  user_email: string;
  device_count: number;
  total_events: number;
  corruption_count: number;
  last_sync: string;
}

/**
 * File history entry
 */
export interface FileHistoryEntry {
  event: SyncEvent;
  content_changed: boolean;
  previous_hash: string | null;
}

/**
 * Query filters for sync events
 */
export interface SyncEventFilters {
  user_email?: string;
  device_code?: string;
  file_id?: string;
  session_id?: string;
  event_type?: EventType | EventType[];
  has_suspicious_chars?: boolean;
  was_quarantined?: boolean;
  quarantine_reason?: QuarantineReason;
  is_first_sync?: boolean;
  firmware_version?: string;
  timestamp_gte?: string;
  timestamp_lte?: string;
  success?: boolean;
  // Deduplication filters (new)
  has_name_deduplication?: boolean;
  is_mass_deduplication?: boolean;
  page?: number;
  page_size?: number;
  ordering?: string;
}

